import { getQueryParameter } from 'ck-utils/index';

const { REACT_APP_ALGOLIA_PREFIX } = process.env;
const INDEX_PRODUCTS = `${REACT_APP_ALGOLIA_PREFIX || ''}products`;

/**
 * Remove expired queries from 'ck_search_analytics_xx' localStorage
 * @param storageKey
 * @param currentStorage
 */
const removeExpiredAnalyticsFromStorage = (storageKey, currentStorage) => {
  if (!storageKey || !currentStorage || !currentStorage.queries) return;

  const { indexName, queries } = currentStorage;
  const cleanedStorage = {
    indexName,
    queries: Object.keys(queries).reduce((acc, queryID) => {
      // only add queries that were performed within an hour
      if (Date.now() - queries[queryID].timeStamp < 3600000) {
        acc[queryID] = queries[queryID];
      }
      return acc;
    }, {})
  };

  localStorage.setItem(storageKey, JSON.stringify(cleanedStorage));
};

/**
 * Add product/collection(objectID) to corresponding queryID in `ck_search_analytics_xx` localStorage
 * @param index
 * @param type - 'products' or 'collections'
 * @param queryID
 * @param objectID
 */
const setLocalStorage = (index, type, queryID, objectID) => {
  const storageKey = type === 'products' ? 'ck_search_analytics' : `ck_search_analytics_${type}`;
  const currentStorage = JSON.parse(localStorage.getItem(storageKey)) || {};
  const newStorage = {
    ...currentStorage,
    queries:
      currentStorage?.queries
        ? {
          ...currentStorage.queries,
          [queryID]: {
            indexName: index,
            [type]:
                currentStorage.queries[queryID] && currentStorage.queries[queryID][type]
                  ? [...currentStorage.queries[queryID][type], objectID]
                  : [objectID],
            timeStamp: Date.now(),
            filters: localStorage.getItem('ck_algolia_filters') || null
          }
        }
        : {
          [queryID]: {
            indexName: index,
            [type]: [objectID],
            timeStamp: Date.now(),
            filters: localStorage.getItem('ck_algolia_filters') || null
          }
        }
  };

  try {
    localStorage.setItem(
      storageKey,
      JSON.stringify(newStorage)
    );
    localStorage.setItem('ck_current_index', index);
  } catch (error) {
    // Quota exceed code 22 is for Chrome and Safari, 1014 is for Firefox.
    if (error.code === 22 || error.code === 1014 || error.name.toLowerCase().indexOf('quota') > -1) {
      removeExpiredAnalyticsFromStorage(storageKey, newStorage);
    } else {
      throw error;
    }
  }
};

// track algolia recommendation click
export const trackAlgoliaRecommendClick = ({
  index = INDEX_PRODUCTS,
  eventName = 'recommendation_click',
  objectID
}) => {
  // catch anything thrown for tracking so the main app doesn't get exception thrown
  try {
    if (objectID) {
      window.aa?.('clickedObjectIDs', {
        index,
        eventName,
        objectIDs: [objectID],
      });
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error found during tracking algolia recommend click', error);
  }
};

// track search results click, could be in results page or search bar, products or collections
export const trackAlgoliaClick = ({
  index = INDEX_PRODUCTS,
  type = 'products',
  eventName,
  queryID,
  objectID,
  position,
}) => {
  // catch anything thrown for tracking so the main app doesn't get exception thrown
  try {
    if (objectID) {
      window.aa?.('clickedObjectIDsAfterSearch', {
        index,
        eventName,
        queryID,
        objectIDs: [objectID],
        positions: [position]
      });
      setLocalStorage(index, type, queryID, objectID);
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error found during tracking algolia search product click', error);
  }
};

export const trackAlgoliaClickedFilter = ({
  index = INDEX_PRODUCTS,
  filters,
}) => {
  try {
    window.aa?.('clickedFilters', {
      index,
      eventName: 'filter_click',
      filters
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error found during tracking algolia clicked filters', error);
  }
};

/**
 * Use GTM to track any custom event in GA
 * @param event
 * @param url
 * @param data
 */
export const trackCustomEventThenRedirect = ({ event, url, data }) => {
  if (window.google_tag_manager) {
    window.dataLayer.push({
      event,
      ...data,

      // Google dataLayer interface that runs when all tags this event fires finished,
      // so we can delay the redirection to partner after the tracking is done
      // https://developers.google.com/tag-manager/enhanced-ecommerce
      eventCallback: url && (() => {
        window.location.href = url;
      }),
      // Set a time delay after which eventCallback is always called to ensure the redirect is not blocked even
      // when GTM tags could stall
      // https://www.simoahava.com/gtm-tips/use-eventtimeout-eventcallback/
      eventTimeout: 2000
    });
  } else if (url) {
    // we still need to redirect if GTM/GA is blocked by browsers and adBlockers
    window.location.href = url;
  }
};

/**
 * Track Search in Google Analytics Events
 * @param searchTerm
 * @param action
 * @param destinationUrl
 */
export const trackSearchEvent = ({ searchTerm, action, destinationUrl }) => {
  if (searchTerm) {
    // Klaviyo Tracking
    window._learnq.push(['track', 'Product Search - Web', {
      'Search query': searchTerm
    }]);
  }

  trackCustomEventThenRedirect({
    event: 'search_actioned',
    url: destinationUrl || `/pages/search-results-page?query=${encodeURIComponent(searchTerm)}`,
    data: {
      siteSearch: {
        action,
        searchTerm: searchTerm || 'no_search_term',
        // TODO: Not sure where to put this logic, but feels wrong to put it here
        destination: action === 'Redirect' && destinationUrl ? ` - ${destinationUrl.split('/').pop()}` : ''
      }
    }
  });
};

/**
 * Normalise product impressions tracking data then send to GTM
 * @param {Array} hits
 * @param {Number} hitsPerPage
 * @param {Number} page - 0 based page number
 * @param {String} listName
 * @param {String} listId
 */
export const trackProductImpressions = (hits, hitsPerPage, page, listName, listId) => {
  window.dataLayer.push({
    event: 'productImpressionsView',
    ecommerce: {
      currencyCode: window.Shopify.currency,
      impressions: [
        ...hits.map((hit, index) => ({
          name: hit.title,
          id: hit.objectID.replace('gid://shopify/Product/', ''),
          price: String(hit.price),
          brand: hit.vendor,
          category: hit.productType,
          list: listName,
          list_id: listId || window.location.pathname.split('/').pop(),
          position: (page * hitsPerPage) + index + 1 // add pagination to the position, page is 0 based
        }))
      ]
    }
  });
};

/**
 * Normalise product click tracking data and send to GTM, then redirect to the clickThrough URL
 * @param {Object} hit
 * @param {String} clickThroughUrl
 * @param {String} listName
 * @param {String} listId
 */
export const trackProductClick = (hit, clickThroughUrl, listName, listId) => {
  trackCustomEventThenRedirect({
    event: 'productClick',
    url: clickThroughUrl,
    data: {
      ecommerce: {
        currencyCode: window.Shopify.currency,
        click: {
          actionField: {
            list: listName,
            list_id: listId,
          },
          products: [{
            name: hit.title,
            id: hit.objectID.replace('gid://shopify/Product/', ''),
            price: String(hit.price),
            brand: hit.vendor,
            category: hit.productType,
            list: listName,
            position: hit.__position
          }]
        }
      }
    }
  });
};

const getCollectionHandle = (url) => new URL(url)?.pathname?.split('/')?.pop();

/**
 * Track facet click events
 * @param data - object containing facet selection data
 */
export const trackFacetClick = (data) => {
  // Get collection handle
  const handle = getCollectionHandle(window.location.href);

  // search-results-page
  const isSearchResult = handle === 'search-results-page';
  const searchTerm = isSearchResult ? getQueryParameter() : null;

  trackCustomEventThenRedirect({
    event: 'facet_selection',
    data: {
      facetSelection: {
        collection: isSearchResult ? searchTerm : handle,
        is_search_result: isSearchResult,
        search_term: searchTerm,
        ...data,
      }
    }
  });
};

/**
 * Track internal link click events
 * @param data - object containing internal link click data
 * @param targetUrl - Url of the internal link
 */
export const trackInternalLinkClick = (data, targetUrl = null) => {
  // Get collection handle
  const handle = getCollectionHandle(window.location.href);

  // search-results-page
  const isSearchResult = handle === 'search-results-page';
  const searchTerm = isSearchResult ? getQueryParameter() : null;

  trackCustomEventThenRedirect({
    event: 'subcat_internal_link',
    url: targetUrl,
    data: {
      subCatInternalLink: {
        source_category: handle,
        target_category: targetUrl ? getCollectionHandle(targetUrl) : null,
        is_search_result: isSearchResult,
        search_term: searchTerm,
        ...data,
      }
    }
  });
};
